import React, { useState, useEffect, useCallback } from 'react';
import { Package, Truck, ChevronDown, ChevronUp, MapPin, User, Clock, CheckCircle, Search } from 'lucide-react';
import { shopApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const STATUS_STYLES = {
  paid: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Unfulfilled', dot: 'bg-amber-400' },
  fulfilled: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'Fulfilled', dot: 'bg-emerald-400' },
};

const CreatorOrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [trackingInput, setTrackingInput] = useState({});
  const [fulfilling, setFulfilling] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const d = await shopApi.getCreatorOrders();
      setOrders(d.orders || []);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleFulfill = async (orderId) => {
    const code = trackingInput[orderId] || '';
    setFulfilling(orderId);
    try {
      await shopApi.fulfillOrder(orderId, code, window.location.origin);
      toast.success('Order marked as fulfilled!');
      fetchOrders();
    } catch (e) { toast.error(e.message || 'Failed to fulfill order'); }
    setFulfilling(null);
  };

  const handleUpdateTracking = async (orderId) => {
    const code = trackingInput[orderId] || '';
    if (!code) { toast.error('Enter a tracking code'); return; }
    setFulfilling(orderId);
    try {
      await shopApi.updateTracking(orderId, code, window.location.origin);
      toast.success('Tracking updated & email sent!');
      fetchOrders();
    } catch (e) { toast.error(e.message || 'Failed'); }
    setFulfilling(null);
  };

  const filtered = orders.filter(o => {
    if (filter === 'unfulfilled' && o.status === 'fulfilled') return false;
    if (filter === 'fulfilled' && o.status !== 'fulfilled') return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (o.user_name || '').toLowerCase().includes(q) ||
        (o.user_email || '').toLowerCase().includes(q) ||
        String(o.order_number).includes(q) ||
        (o.tracking_code || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const unfulfilledCount = orders.filter(o => o.status !== 'fulfilled').length;
  const fulfilledCount = orders.filter(o => o.status === 'fulfilled').length;

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="creator-orders-panel">
      {/* Header + Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-[#8B5CF6]" /> Orders
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">{orders.length} total orders</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            <span className="text-gray-400">{unfulfilledCount} unfulfilled</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
            <span className="text-gray-400">{fulfilledCount} fulfilled</span>
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, order #…"
            className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-9 text-sm"
            data-testid="orders-search" />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: 'all', label: 'All' },
            { key: 'unfulfilled', label: 'Unfulfilled' },
            { key: 'fulfilled', label: 'Fulfilled' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.key ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'bg-white/[0.03] text-gray-400 hover:text-white'}`}
              data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Order List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 admin-card">
          <Package className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">{orders.length === 0 ? 'No orders yet' : 'No orders match filters'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(order => {
            const st = STATUS_STYLES[order.status] || STATUS_STYLES.paid;
            const isOpen = expanded === order.order_id;
            return (
              <div key={order.order_id}
                className={`rounded-xl border transition-colors ${isOpen ? 'border-[#8B5CF6]/30 bg-[#0F0B1E]' : 'border-white/5 bg-[#0a0618]'}`}
                data-testid={`creator-order-${order.order_id}`}>
                {/* Order Row */}
                <button onClick={() => setExpanded(isOpen ? null : order.order_id)}
                  className="w-full p-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-4">
                    {/* Status indicator bar */}
                    <div className={`w-1 h-12 rounded-full ${st.dot}`}></div>
                    <div className={`w-10 h-10 rounded-lg ${st.bg} flex items-center justify-center font-bold ${st.text} text-sm`}>
                      #{order.order_number}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{order.user_name || order.user_email}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' — '}
                        {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.bg} ${st.text} border ${st.border}`}>
                      {st.label}
                    </span>
                    <span className="font-semibold text-sm">${order.amount?.toFixed(2)}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                  </div>
                </button>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-white/5 p-4 space-y-4">
                    {/* Customer info */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-white/[0.02] space-y-1.5">
                        <p className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                          <User className="w-3 h-3" /> Customer
                        </p>
                        <p className="text-sm font-medium">{order.user_name || '—'}</p>
                        <p className="text-xs text-gray-400">{order.user_email}</p>
                      </div>
                      {order.shipping && (
                        <div className="p-3 rounded-lg bg-white/[0.02] space-y-1.5">
                          <p className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                            <MapPin className="w-3 h-3" /> Shipping Address
                          </p>
                          <p className="text-sm">{order.shipping.full_name}</p>
                          <p className="text-xs text-gray-300">
                            {order.shipping.address}
                            {order.shipping.address_line2 ? `, ${order.shipping.address_line2}` : ''}
                          </p>
                          <p className="text-xs text-gray-300">
                            {order.shipping.postal_code} {order.shipping.city}, {order.shipping.country}
                          </p>
                          <p className="text-xs text-gray-400">Phone: {order.shipping.phone}</p>
                        </div>
                      )}
                    </div>

                    {/* Items */}
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-2">Items</p>
                      <div className="space-y-1.5">
                        {(order.items || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-white/[0.02] rounded-lg">
                            {item.image && <img src={item.image.startsWith('/') ? `${window.location.origin}${item.image}` : item.image}
                              alt="" className="w-10 h-10 rounded object-cover" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.product_name}</p>
                              {Object.keys(item.selected_variants || {}).length > 0 && (
                                <p className="text-[11px] text-[#8B5CF6]">
                                  {Object.entries(item.selected_variants).map(([k, v]) => `${k}: ${v}`).join(' / ')}
                                </p>
                              )}
                            </div>
                            <span className="text-sm text-gray-400">×{item.quantity}</span>
                            <span className="text-sm font-medium">${item.line_total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tracking + Fulfillment */}
                    <div className="p-3 rounded-lg bg-white/[0.02] space-y-3">
                      <p className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                        <Truck className="w-3 h-3" /> Tracking & Fulfillment
                      </p>
                      {order.tracking_code && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-400">Current tracking:</span>
                          <span className="font-mono text-gray-300 bg-white/5 px-2 py-0.5 rounded">{order.tracking_code}</span>
                        </div>
                      )}
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <input
                            value={trackingInput[order.order_id] ?? order.tracking_code ?? ''}
                            onChange={e => setTrackingInput(p => ({ ...p, [order.order_id]: e.target.value }))}
                            placeholder="Enter tracking code"
                            className="w-full px-3 py-2 rounded-lg bg-[#030014] border border-white/10 text-sm focus:border-[#8B5CF6] outline-none"
                            data-testid={`tracking-input-${order.order_id}`}
                          />
                        </div>
                        {order.status !== 'fulfilled' ? (
                          <Button
                            onClick={() => handleFulfill(order.order_id)}
                            disabled={fulfilling === order.order_id}
                            className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20 whitespace-nowrap h-10"
                            data-testid={`fulfill-btn-${order.order_id}`}>
                            <CheckCircle className="w-4 h-4 mr-1.5" />
                            {fulfilling === order.order_id ? 'Fulfilling…' : 'Mark Fulfilled'}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleUpdateTracking(order.order_id)}
                            disabled={fulfilling === order.order_id}
                            className="bg-[#8B5CF6]/15 text-[#8B5CF6] hover:bg-[#8B5CF6]/25 border border-[#8B5CF6]/20 whitespace-nowrap h-10"
                            data-testid={`update-tracking-btn-${order.order_id}`}>
                            <Truck className="w-4 h-4 mr-1.5" />
                            {fulfilling === order.order_id ? 'Updating…' : 'Update Tracking'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="flex flex-wrap gap-4 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Placed: {new Date(order.created_at).toLocaleString('en-US')}</span>
                      {order.fulfilled_at && (
                        <span className="flex items-center gap-1 text-emerald-400/70"><CheckCircle className="w-3 h-3" /> Fulfilled: {new Date(order.fulfilled_at).toLocaleString('en-US')}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CreatorOrdersPanel;
